import { useQuery } from '@tanstack/react-query'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts'
import { TrendingUp, Smartphone, Apple } from 'lucide-react'

interface StatsData {
  total_downloads: number
  by_platform: Record<string, number>
  by_app: Record<string, number>
  by_day: Record<string, number>
}

const COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6']

export default function Analytics() {
  const { data: stats, isLoading } = useQuery<StatsData>({
    queryKey: ['analytics'],
    queryFn: async () => {
      const res = await fetch('/api/analytics/stats?days=30')
      return res.json()
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" />
      </div>
    )
  }

  // Transform data for charts
  const dailyData = Object.entries(stats?.by_day || {})
    .map(([date, count]) => ({ date: date.slice(5), downloads: count }))
    .slice(-14) // Last 14 days

  const platformData = Object.entries(stats?.by_platform || {}).map(([platform, count]) => ({
    name: platform.charAt(0).toUpperCase() + platform.slice(1),
    value: count,
  }))

  const appData = Object.entries(stats?.by_app || {})
    .map(([app, count]) => ({ name: app, downloads: count }))
    .sort((a, b) => b.downloads - a.downloads)
    .slice(0, 5)

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold mb-2">Analytics</h2>
        <p className="text-gray-400">Download statistics for the last 30 days</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
        <div className="bg-white/5 rounded-xl p-4 md:p-6 border border-white/10">
          <div className="flex items-center gap-2 md:gap-3">
            <TrendingUp className="w-7 h-7 md:w-8 md:h-8 text-primary-500 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-gray-400 text-xs md:text-sm">Total Downloads</p>
              <p className="text-2xl md:text-3xl font-bold">{stats?.total_downloads || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white/5 rounded-xl p-4 md:p-6 border border-white/10">
          <div className="flex items-center gap-2 md:gap-3">
            <Smartphone className="w-7 h-7 md:w-8 md:h-8 text-green-500 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-gray-400 text-xs md:text-sm">Android Downloads</p>
              <p className="text-2xl md:text-3xl font-bold">{stats?.by_platform?.android || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white/5 rounded-xl p-4 md:p-6 border border-white/10 sm:col-span-2 md:col-span-1">
          <div className="flex items-center gap-2 md:gap-3">
            <Apple className="w-7 h-7 md:w-8 md:h-8 text-gray-400 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-gray-400 text-xs md:text-sm">iOS Downloads</p>
              <p className="text-2xl md:text-3xl font-bold">{stats?.by_platform?.ios || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* Daily Downloads Chart */}
        <div className="bg-white/5 rounded-xl p-4 md:p-6 border border-white/10">
          <h3 className="text-base md:text-lg font-semibold mb-3 md:mb-4">Downloads Over Time</h3>
          {dailyData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="date" stroke="#9ca3af" />
                <YAxis stroke="#9ca3af" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1f2937',
                    border: '1px solid #374151',
                    borderRadius: '8px',
                  }}
                />
                <Bar dataKey="downloads" fill="#6366f1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-500">
              No data available
            </div>
          )}
        </div>

        {/* Platform Distribution */}
        <div className="bg-white/5 rounded-xl p-4 md:p-6 border border-white/10">
          <h3 className="text-base md:text-lg font-semibold mb-3 md:mb-4">Platform Distribution</h3>
          {platformData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={platformData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {platformData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1f2937',
                    border: '1px solid #374151',
                    borderRadius: '8px',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-500">
              No data available
            </div>
          )}
        </div>
      </div>

      {/* Top Apps Table */}
      <div className="bg-white/5 rounded-xl p-4 md:p-6 border border-white/10">
        <h3 className="text-base md:text-lg font-semibold mb-3 md:mb-4">Top Apps by Downloads</h3>
        {appData.length > 0 ? (
          <div className="overflow-x-auto max-w-full">
            <table className="w-full text-xs md:text-sm min-w-max md:min-w-0">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-2 md:py-3 px-2 md:px-4 text-gray-400 font-medium">Rank</th>
                  <th className="text-left py-2 md:py-3 px-2 md:px-4 text-gray-400 font-medium">App</th>
                  <th className="text-right py-2 md:py-3 px-2 md:px-4 text-gray-400 font-medium">Downloads</th>
                  <th className="text-right py-2 md:py-3 px-2 md:px-4 text-gray-400 font-medium hidden sm:table-cell">Share</th>
                </tr>
              </thead>
              <tbody>
                {appData.map((app, idx) => (
                  <tr key={app.name} className="border-b border-white/5">
                    <td className="py-2 md:py-3 px-2 md:px-4 text-gray-500">{idx + 1}</td>
                    <td className="py-2 md:py-3 px-2 md:px-4 font-medium">{app.name}</td>
                    <td className="py-2 md:py-3 px-2 md:px-4 text-right text-primary-400">{app.downloads}</td>
                    <td className="py-2 md:py-3 px-2 md:px-4 text-right text-gray-400 hidden sm:table-cell">
                      {((app.downloads / (stats?.total_downloads || 1)) * 100).toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">No download data yet</p>
        )}
      </div>
    </div>
  )
}
